/* ==========================================================================
   CLARITY HEALTH CO — HEADER / NAVIGATION behaviour  (Phase 3)
   Vanilla ES module, no external libraries. Progressive enhancement: the
   header, nav and search form all work without JS; this adds the floating
   state, overlay drawers (focus-trapped), mega-menu panes and the AJAX cart.
   Reuses Horizon's scroll-lock mechanism (html[scroll-lock]) rather than
   adding a second one.
   ========================================================================== */
(function () {
  const root =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const cartChangeUrl = `${root}cart/change.js`.replace('//', '/');
  const cartAddUrl = `${root}cart/add.js`.replace('//', '/');
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* -------- pristine (un-wrapped) fetch for cart writes --------------------
     Appstle wraps window.fetch and rewrites the selling_plan on /cart/add from
     its own hidden widget state — which the theme's custom "Pick your frequency"
     selector does not drive — so a One-Time add was being coerced to the
     subscription (and, in reverse, a subscribe add dropped to one-time). Cart
     writes therefore go through a NATIVE fetch taken from a same-origin
     about:blank iframe, so the exact plan the customer chose in the theme is
     what gets sent. Falls back to the normal fetch if the iframe is blocked. */
  let _cartFrame = null;
  function cartFetch(url, opts) {
    try {
      if (!_cartFrame || !_cartFrame.contentWindow) {
        _cartFrame = document.createElement('iframe');
        _cartFrame.setAttribute('aria-hidden', 'true');
        _cartFrame.tabIndex = -1;
        _cartFrame.style.cssText = 'position:absolute;width:0;height:0;border:0;opacity:0;pointer-events:none';
        (document.body || document.documentElement).appendChild(_cartFrame);
      }
      const f = _cartFrame.contentWindow && _cartFrame.contentWindow.fetch;
      if (f && /\[native code\]/.test(f.toString())) return f.call(_cartFrame.contentWindow, url, opts);
    } catch (e) { /* fall through to the wrapped fetch */ }
    return window.fetch(url, opts);
  }
  const FOCUSABLE =
    'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  /* -------- scroll lock (reuses Horizon's html[scroll-lock] CSS) ---------- */
  let lockCount = 0;
  const lock = () => { lockCount++; document.documentElement.setAttribute('scroll-lock', ''); };
  const unlock = () => { lockCount = Math.max(0, lockCount - 1); if (!lockCount) document.documentElement.removeAttribute('scroll-lock'); };

  /* -------- Overlay: a focus-trapped drawer / modal ---------------------- */
  class Overlay {
    constructor(el) {
      this.el = el;
      this.panel = el.querySelector('[data-overlay-panel]') || el;
      this.trigger = null;
      this.open = false;
      el.addEventListener('click', (e) => {
        if (e.target.closest('[data-clarity-close]') || e.target === el || e.target.hasAttribute('data-overlay-scrim')) {
          this.close();
        }
      });
    }
    show(trigger) {
      if (this.open) return;
      this.open = true;
      this.trigger = trigger || null;
      this.el.classList.add('is-open');
      this.el.removeAttribute('hidden');
      lock();
      // focus first field/control inside the panel
      requestAnimationFrame(() => {
        const first = this.panel.querySelector('[data-overlay-autofocus]') || this.panel.querySelector(FOCUSABLE);
        if (first) first.focus();
      });
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      this._onKey = (e) => this._key(e);
      document.addEventListener('keydown', this._onKey);
    }
    close() {
      if (!this.open) return;
      this.open = false;
      this.el.classList.remove('is-open');
      unlock();
      document.removeEventListener('keydown', this._onKey);
      if (this.trigger) {
        this.trigger.setAttribute('aria-expanded', 'false');
        this.trigger.focus();
      }
    }
    _key(e) {
      if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
      if (e.key !== 'Tab') return;
      const items = Array.from(this.panel.querySelectorAll(FOCUSABLE)).filter((n) => n.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  /* -------- <clarity-header>: sticky state + overlays + mega ------------- */
  class ClarityHeader extends HTMLElement {
    connectedCallback() {
      // Floating → stuck shadow. Works with both the window and Horizon's
      // .page-wrapper scroll container (desktop).
      const onScroll = () => {
        const wrapper = document.querySelector('.page-wrapper');
        const y = (wrapper && wrapper.scrollTop) || window.scrollY || 0;
        this.classList.toggle('is-stuck', y > 6);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      const wrapper = document.querySelector('.page-wrapper');
      if (wrapper) wrapper.addEventListener('scroll', onScroll, { passive: true });
      onScroll();

      // Overlays living in this section
      this.menu = this._overlay('clarity-menu-drawer');
      this.search = this._overlay('clarity-search');

      this._bindTriggers();
      this._mega();
    }

    _overlay(id) {
      const el = this.querySelector(`#${id}`) || document.getElementById(id);
      return el ? new Overlay(el) : null;
    }

    _bindTriggers() {
      this.querySelectorAll('[data-clarity-menu-open]').forEach((b) =>
        b.addEventListener('click', () => this.menu && this.menu.show(b)));
      this.querySelectorAll('[data-clarity-search-open]').forEach((b) =>
        b.addEventListener('click', () => this.search && this.search.show(b)));
      // Cart open is global (also reachable from other sections)
      this.querySelectorAll('[data-clarity-cart-open]').forEach((b) =>
        b.addEventListener('click', (e) => { e.preventDefault(); window.ClarityCart && window.ClarityCart.open(b); }));

      // Mobile drawer accordion (nested nav)
      this.querySelectorAll('[data-drawer-accordion]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const open = btn.getAttribute('aria-expanded') === 'true';
          btn.setAttribute('aria-expanded', String(!open));
          const panel = document.getElementById(btn.getAttribute('aria-controls'));
          if (panel) panel.hidden = open;
        });
      });
    }

    // Mega menu: rail items swap panes; hover/focus is CSS, keyboard here.
    _mega() {
      this.querySelectorAll('[data-mega]').forEach((mega) => {
        const rail = mega.querySelectorAll('[data-mega-tab]');
        const panes = mega.querySelectorAll('[data-mega-pane]');
        const activate = (i) => {
          rail.forEach((t, n) => t.setAttribute('aria-selected', String(n === i)));
          panes.forEach((p, n) => p.classList.toggle('is-active', n === i));
        };
        rail.forEach((tab, i) => {
          const go = () => activate(i);
          tab.addEventListener('mouseenter', go);
          tab.addEventListener('focus', go);
          tab.addEventListener('click', go);
        });
        // Esc closes the (hover/focus) mega by moving focus out
        mega.closest('[data-nav-item]')?.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') mega.closest('[data-nav-item]').querySelector('[data-nav-link]')?.focus();
        });
      });
    }
  }
  if (!customElements.get('clarity-header')) customElements.define('clarity-header', ClarityHeader);

  /* -------- Cart: standard /cart/change.js + Section Rendering ----------- */
  const ClarityCart = {
    overlay: null,
    el() { return document.getElementById('clarity-cart-drawer'); },
    init() {
      const el = this.el();
      if (el && !this.overlay) this.overlay = new Overlay(el);
      this._bind();
    },
    open(trigger) {
      if (!this.overlay) this.init();
      if (this.overlay) this.overlay.show(trigger);
      this.refresh();
    },
    _bind() {
      const el = this.el();
      if (!el || el.dataset.bound) return;
      el.dataset.bound = '1';
      el.addEventListener('click', (e) => {
        const dec = e.target.closest('[data-cart-minus]');
        const inc = e.target.closest('[data-cart-plus]');
        const rm = e.target.closest('[data-cart-remove]');
        const node = e.target.closest('[data-cart-line]');
        if (!node) return;
        const line = Number(node.dataset.cartLine);
        const qty = Number(node.dataset.cartQty || 1);
        if (dec) this.change(line, Math.max(0, qty - 1));
        else if (inc) this.change(line, qty + 1);
        else if (rm) this.change(line, 0);
      });
    },
    async refresh() {
      try {
        const res = await fetch(`${root}?section_id=clarity-cart-drawer`);
        if (!res.ok) return;
        this._swap(await res.text(), null);
      } catch (e) { /* offline / non-blocking */ }
    },
    async change(line, quantity) {
      const el = this.el();
      if (el) el.classList.add('is-busy');
      try {
        const res = await fetch(cartChangeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity, sections: 'clarity-cart-drawer', sections_url: window.location.pathname }),
        });
        const data = await res.json();
        if (data.sections && data.sections['clarity-cart-drawer']) this._swap(data.sections['clarity-cart-drawer'], data.item_count);
        this._count(data.item_count);
      } catch (e) { /* keep prior DOM */ }
      if (el) el.classList.remove('is-busy');
    },
    // Add a variant to the REAL cart, then open the Clarity drawer (which
    // refreshes the drawer + badge). Used by the Buy and Featured sections.
    async add(id, quantity, sellingPlan) {
      if (!id) return false;
      const body = { id: Number(id), quantity: Number(quantity) || 1 };
      if (sellingPlan) body.selling_plan = Number(sellingPlan);
      try {
        const res = await cartFetch(cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          document.dispatchEvent(new CustomEvent('cart:error', { detail: err }));
          return false;
        }
        this.open();
        return true;
      } catch (e) { return false; }
    },
    // Add via a real product form (FormData carries id, quantity, selling_plan
    // and any app/line-item fields — e.g. Appstle), then open the drawer.
    async addFromForm(form) {
      if (!form) return false;
      try {
        const res = await cartFetch(cartAddUrl, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          document.dispatchEvent(new CustomEvent('cart:error', { detail: err }));
          return false;
        }
        this.open();
        return true;
      } catch (e) { return false; }
    },
    _swap(html, count) {
      const el = this.el();
      if (!el) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.getElementById('clarity-cart-drawer');
      const body = el.querySelector('[data-cart-body]');
      const freshBody = fresh && fresh.querySelector('[data-cart-body]');
      if (body && freshBody) body.replaceWith(freshBody);
      const c = fresh ? fresh.getAttribute('data-count') : null;
      this._count(count != null ? count : c);
    },
    _count(n) {
      if (n == null) return;
      document.querySelectorAll('[data-clarity-cart-count]').forEach((b) => {
        b.textContent = n;
        b.classList.toggle('is-on', Number(n) > 0);
      });
    },
  };
  window.ClarityCart = ClarityCart;
  document.addEventListener('DOMContentLoaded', () => ClarityCart.init());
  if (document.readyState !== 'loading') ClarityCart.init();
  // Any section can refresh the badge + drawer after a cart mutation.
  ['cart:refresh', 'cart:updated', 'cart:change'].forEach((ev) =>
    document.addEventListener(ev, (e) => {
      if (e && e.detail && e.detail.open) ClarityCart.open();
      else ClarityCart.refresh();
    })
  );

  // Global add-to-cart: any [data-clarity-add] button adds its variant to the
  // real cart. The Buy section keeps its button's dataset (variant/qty/plan) in
  // sync as the shopper changes options; Featured cards ship static values.
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-clarity-add]');
    if (!btn) return;
    if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
    const id = btn.dataset.variantId;
    if (!id) return;
    e.preventDefault();
    btn.classList.add('is-loading');
    ClarityCart.add(id, btn.dataset.quantity || 1, btn.dataset.sellingPlan || '')
      .finally(() => btn.classList.remove('is-loading'));
  });
})();
